import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Paper, Typography, ToggleButtonGroup, ToggleButton, Stack } from '@mui/material';
import { useNavigate } from 'react-router';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import { useAppDispatch } from '../../../../store/store';
import { parentUrl } from '../../../../utils/constants';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { fetchTeams } from '../../../../store/careTeams.slice';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { useWidgetContext } from '../../../../hooks/widgetContext';
import { dateFormatter } from '../../../../utils/timeFormat';
import Link from '@mui/material/Link';
import { fetchMedicalDocumentsToReview, exportMedicalDocumentsToReview } from '../../../Widgets/WidgetService';

const MedicalDocumentsToReviewDetail = () => {
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [recentMedicalDocuments, setRecentMedicalDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const {
        documentTimePeriod,
        setDocumentTimePeriod,
        documentSelectedTeam,
        setDocumentSelectedTeam,
        documentShowInActiveTeams,
        setDocumentShowInActiveTeams
    } = useWidgetContext();

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const timePeriods = [
        { label: '7 Days', value: 1 },
        { label: '15 Days', value: 2 },
        { label: '30 Days', value: 3 },
    ];

    useEffect(() => {
        const changeFilter = async () => {
            try {
                setIsLoading(true);
                const response = await fetchMedicalDocumentsToReview(documentTimePeriod, documentSelectedTeam, documentShowInActiveTeams);
                setRecentMedicalDocuments(response);
                setIsLoading(false);
            } catch (error) {
                setRecentMedicalDocuments([]);
                setIsLoading(false);
                console.log(error);
            }
        };
        changeFilter();
    }, [documentTimePeriod, documentSelectedTeam, documentShowInActiveTeams]);

    const exportMedicalDocumentToReview = async () => {
        setDownLoading(true);
        try {
            const response = await exportMedicalDocumentsToReview(documentTimePeriod, documentSelectedTeam, documentShowInActiveTeams);
            let blob = b64toBlob(response.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            let url = window.URL.createObjectURL(blob);
            let link = document.createElement('a');
            link.href = url;
            var date = new Date();
            var mm = date.getMonth() + 1; // getMonth() is zero-based
            var dd = date.getDate();
            var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
            link.setAttribute('download', `MedicalDocumentsToReview - ${dateInfo}.xlsx`);
            link.click();
            link.remove();
        } catch (error) {
            console.error(error);
        } finally {
            setDownLoading(false);
        }
    };

    const backToWidget = useCallback(() => {
        history('/provider/metricsummary/smart-dashboard');
    }, [history]);

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14}>
                        {params.api.getSortedRowIds().indexOf(params.id) + 1}
                    </Typography>
                );
            },
        },
        {
            field: 'AhcccsId',
            headerName: 'Ahcccs Id',
            width: 150,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14}>
                        {params.row.AhcccsId}
                    </Typography>
                );
            },
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            width: 220,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14}>
                        {params.row.MemberName}
                    </Typography>
                );
            },
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            width: 150,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14}>
                        {params.row.DOB ? dateFormatter(new Date(params.row.DOB)) : '-'}
                    </Typography>
                );
            },
        },
        {
            field: 'MetaTags',
            headerName: 'Meta Tag',
            width: 180,
            renderCell: (params) => {
                return (
                    <>
                        <Stack>
                            {params.row.MetaTags?.map((tag) => {
                                return <label key={tag.Id}>{tag.Name}</label>;
                            })}
                        </Stack>
                    </>
                );
            },
        },
        {
            field: 'CreationTime',
            headerName: 'Uploaded On',
            width: 150,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14}>
                        {params.row.CreationTime ? dateFormatter(new Date(params.row.CreationTime)) : '-'}
                    </Typography>
                );
            },
        },
        {
            field: 'UploadedBy',
            headerName: 'Uploaded By',
            width: 220,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14}>
                        {params.row.UploadedBy?.Name ?? '-'}
                    </Typography>
                );
            },
        },
        {
            field: 'AzureDocumentUri',
            headerName: 'Document Name',
            width: 330,
            renderCell: (params) => {
                return (
                    <Link href={params.row.AzureDocumentUri} target="_blank" onClick={(e) => e.stopPropagation()}>
                        {params.row.Name}
                    </Link>
                );
            },
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={2}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Medical Documents to Review
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={1} spacing={2}>
                    <Grid size={2}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={documentSelectedTeam}
                            onChange={(id) => setDocumentSelectedTeam(id)}
                            showInactive={documentShowInActiveTeams}
                            onToggleInactive={setDocumentShowInActiveTeams}
                        />
                    </Grid>
                    <Grid size={3}>
                        <ToggleButtonGroup
                            value={documentTimePeriod}
                            exclusive
                            disabled={documentSelectedTeam === '-1'}
                            size="small"
                            onChange={(event: React.MouseEvent<HTMLElement>, newPeriod: number | null) => {
                                if (newPeriod !== null) {
                                    setDocumentTimePeriod(newPeriod);
                                }
                            }}
                        >
                            {timePeriods.map((period) => {
                                return (
                                    <ToggleButton color="primary" key={period.value} value={period.value}>
                                        {period.label}
                                    </ToggleButton>
                                );
                            })}
                        </ToggleButtonGroup>
                    </Grid>
                    <Grid size={5.8} />
                    <Grid size={1}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportMedicalDocumentToReview()}
                            variant="contained"
                            color="success"
                            disabled={downLoading || recentMedicalDocuments?.length === 0}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} marginTop={3}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isLoading ? (
                            <Grid container columnSpacing={3}>
                                <Grid size={12} sx={{ height: '67vh', width: '100%' }}>
                                    <DataGrid
                                        sx={{
                                            '& .MuiDataGrid-cell': {
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                        }}
                                        columns={columns}
                                        rows={recentMedicalDocuments}
                                        getRowId={(row) => row.Id ?? `${row.MemberId}-${row.DOB}`}
                                        paginationModel={{ pageSize: pageSize, page: page }}
                                        pageSizeOptions={[5]}
                                        onPaginationModelChange={(model) => {
                                            setPageSize(model.pageSize);
                                            setPage(model.page);
                                        }}
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.MemberId}/documents`, '_blank');
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        ) : (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default MedicalDocumentsToReviewDetail;
